import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, Image as ImageIcon, Music, Video, FileIcon } from "lucide-react";

interface MediaPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (url: string) => void;
  bucket?: string;
  folder?: string;
  mediaType?: "audio" | "video" | "image";
  onlyFolder?: boolean;
}

export const MediaPicker = ({
  open,
  onOpenChange,
  onSelect,
  bucket = "demos",
  folder = "images",
  mediaType = "image",
  onlyFolder = false,
}: MediaPickerProps) => {
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);

  const { data: files, isLoading } = useQuery({
    queryKey: ["media-files", bucket, folder, mediaType, onlyFolder],
    queryFn: async () => {
      const allFiles: { name: string; url: string; path: string }[] = [];
      
      const isMatchingMediaType = (filename: string) => {
        const ext = filename.split(".").pop()?.toLowerCase();
        const isAudio = ["mp3", "wav", "ogg", "m4a", "aac", "flac"].includes(ext || "");
        const isVideo = ["mp4", "webm", "mov", "avi", "mkv"].includes(ext || "");
        const isImage = ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext || "");

        return (
          (mediaType === "audio" && isAudio) ||
          (mediaType === "video" && isVideo) ||
          (mediaType === "image" && isImage)
        );
      };

      // List from the specific folder first
      const { data: folderData } = await supabase.storage
        .from(bucket)
        .list(folder, { sortBy: { column: "created_at", order: "desc" } });

      if (folderData) {
        folderData
          .filter((file) => file.name !== ".emptyFolderPlaceholder" && file.metadata !== null)
          .filter((file) => isMatchingMediaType(file.name))
          .forEach((file) => {
            allFiles.push({
              name: file.name,
              path: `${folder}/${file.name}`,
              url: supabase.storage.from(bucket).getPublicUrl(`${folder}/${file.name}`).data.publicUrl,
            });
          });
      }

      // If onlyFolder is true, don't search root or other subfolders
      if (onlyFolder) {
        return allFiles;
      }

      // Also list from root of bucket for files directly there
      const { data: rootData } = await supabase.storage
        .from(bucket)
        .list("", { sortBy: { column: "created_at", order: "desc" } });

      if (rootData) {
        // Add direct files from root
        rootData
          .filter((file) => file.name !== ".emptyFolderPlaceholder" && file.metadata !== null)
          .filter((file) => isMatchingMediaType(file.name))
          .forEach((file) => {
            allFiles.push({
              name: file.name,
              path: file.name,
              url: supabase.storage.from(bucket).getPublicUrl(file.name).data.publicUrl,
            });
          });

        // Check subfolders for more files
        const subfolders = rootData.filter((item) => item.metadata === null && item.name !== folder);
        for (const subfolder of subfolders) {
          const { data: subfolderData } = await supabase.storage
            .from(bucket)
            .list(subfolder.name, { sortBy: { column: "created_at", order: "desc" } });

          if (subfolderData) {
            subfolderData
              .filter((file) => file.name !== ".emptyFolderPlaceholder" && file.metadata !== null)
              .filter((file) => isMatchingMediaType(file.name))
              .forEach((file) => {
                allFiles.push({
                  name: `${subfolder.name}/${file.name}`,
                  path: `${subfolder.name}/${file.name}`,
                  url: supabase.storage.from(bucket).getPublicUrl(`${subfolder.name}/${file.name}`).data.publicUrl,
                });
              });
          }
        }
      }

      return allFiles;
    },
    enabled: open,
  });

  const handleConfirm = () => {
    if (selectedUrl) {
      onSelect(selectedUrl);
      onOpenChange(false);
      setSelectedUrl(null);
    }
  };

  const getMediaIcon = () => {
    switch (mediaType) {
      case "audio": return <Music size={48} className="mb-4 opacity-50" />;
      case "video": return <Video size={48} className="mb-4 opacity-50" />;
      default: return <ImageIcon size={48} className="mb-4 opacity-50" />;
    }
  };

  const getMediaLabel = () => {
    switch (mediaType) {
      case "audio": return "audio files";
      case "video": return "videos";
      default: return "images";
    }
  };

  const renderMediaPreview = (file: { name: string; url: string }) => {
    const isSelected = selectedUrl === file.url;
    
    if (mediaType === "image") {
      return (
        <button
          key={file.name}
          type="button"
          onClick={() => setSelectedUrl(file.url)}
          className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
            isSelected
              ? "border-primary ring-2 ring-primary/20"
              : "border-transparent hover:border-muted-foreground/30"
          }`}
        >
          <img
            src={file.url}
            alt={file.name}
            className="w-full h-full object-cover"
          />
          {isSelected && (
            <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
              <Check className="text-primary bg-white rounded-full p-1" size={24} />
            </div>
          )}
        </button>
      );
    }

    // Audio/Video preview
    return (
      <button
        key={file.name}
        type="button"
        onClick={() => setSelectedUrl(file.url)}
        className={`relative flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
          isSelected
            ? "border-primary ring-2 ring-primary/20 bg-primary/5"
            : "border-muted hover:border-muted-foreground/30 bg-muted/50"
        }`}
      >
        {mediaType === "audio" ? (
          <Music size={32} className="text-blue-500 mb-2" />
        ) : (
          <Video size={32} className="text-purple-500 mb-2" />
        )}
        <p className="text-xs text-center truncate w-full">{file.name}</p>
        {isSelected && (
          <div className="absolute top-2 right-2">
            <Check className="text-primary bg-white rounded-full p-0.5" size={18} />
          </div>
        )}
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Select from Media Library</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-[300px]">
          {isLoading ? (
            <div className="grid grid-cols-4 gap-3">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-lg" />
              ))}
            </div>
          ) : files && files.length > 0 ? (
            <div className={`grid gap-3 ${mediaType === "image" ? "grid-cols-4" : "grid-cols-3"}`}>
              {files.map((file) => renderMediaPreview(file))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-12">
              {getMediaIcon()}
              <p>No {getMediaLabel()} in library yet.</p>
              <p className="text-sm">Upload {getMediaLabel()} first.</p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedUrl}>
            Select
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
